// Sorte de feuille « géométrie déductive » (lot I6, plugin `geometry`, objectif `prove`) : chaque ligne est UNE
// propriété (« (IJ) // (BC) », « I milieu de [AB] », « ABCD losange », « ABC rectangle en A »…) ou UNE relation
// entre longueurs (« IJ = BC ÷ 2 », « BC² = AB² + AC² »). La FIGURE (coordonnées du modèle) dit si elle est vraie ;
// une règle déclarative du pack (`geometry.rules`, motifs « ?I milieu de [?A?B] ») ou un calcul sur les longueurs
// connues dit si elle est justifiée. Aucun cas d'exercice ici : les règles sont des données.

import { parse, withIdentifiers } from './syntax.js';
import { isLeaf } from './ast.js';
import { light, keyOf } from './rewrite.js';
import { evaluate, toF } from './equiv.js';

const PT = "[A-Z][′'’]?[₀-₉]*";
const PTV = `(?:\\?[A-Z]|${PT})`;
const PTS = new RegExp(PTV, 'gu');
const ptsOf = (s) => String(s).match(PTS) ?? [];
const one = (s) => {
  const p = ptsOf(s);
  return p.length === 1 && p[0] === s.trim() ? p[0] : null;
};
const two = (s) => {
  const p = ptsOf(s);
  return p.length === 2 && p.join('') === s.replace(/\s/g, '') ? p : null;
};

const SHAPES = ['parallélogramme', 'rectangle', 'losange', 'carré'];

/** Lit une propriété : { k, … } ; { k: 'metric', text } pour une relation de longueurs ; null sinon. */
export function parseProp(text) {
  const p = parseProp0(text);
  return p ? { ...p, text: String(text).trim() } : null;
}
function parseProp0(text) {
  const s = String(text).trim().replace(/'/g, '’');
  let m;
  if ((m = /^\((.+?)\)\s*(\/\/|∥|⊥|∦)\s*\((.+?)\)$/u.exec(s))) {
    const a = two(m[1]),
      b = two(m[3]);
    if (!a || !b) return null;
    return { k: m[2] === '⊥' ? 'perp' : m[2] === '∦' ? 'npar' : 'par', a, b };
  }
  if ((m = new RegExp(`^(${PTV}) milieu de \\[(.+?)\\]$`, 'u').exec(s))) {
    const seg = two(m[2]);
    return seg ? { k: 'mid', i: m[1], seg } : null;
  }
  if ((m = new RegExp(`^(${PTV}) symétrique de (${PTV}) par rapport à (${PTV})$`, 'u').exec(s)))
    return { k: 'sym', m: m[1], a: m[2], o: m[3] };
  if (
    (m = new RegExp(
      `^(${PTV}) ∈ (médiatrice de |cercle de diamètre )?([[(])(.+?)[\\])]$`,
      'u',
    ).exec(s))
  ) {
    const seg = two(m[4]);
    if (!seg) return null;
    const k = m[2]?.startsWith('médiatrice')
      ? 'bis'
      : m[2]
        ? 'circ'
        : m[3] === '['
          ? 'onseg'
          : 'online';
    return { k, p: m[1], seg };
  }
  if ((m = /^(\S+) (parallélogramme|rectangle|losange|carré)$/u.exec(s))) {
    const p = ptsOf(m[1]);
    return p.length === 4 && p.join('') === m[1] ? { k: 'quad', shape: m[2], pts: p } : null;
  }
  if ((m = new RegExp(`^(\\S+) (rectangle|isocèle) en (${PTV})$`, 'u').exec(s))) {
    const p = ptsOf(m[1]);
    if (p.length !== 3 || p.join('') !== m[1] || !p.includes(m[3])) return null;
    return {
      k: m[2] === 'rectangle' ? 'rt' : 'iso',
      at: m[3],
      others: p.filter((x) => x !== m[3]),
    };
  }
  if ((m = /^(\S+) n’est pas rectangle$/u.exec(s))) {
    const p = ptsOf(m[1]);
    return p.length === 3 && p.join('') === m[1] ? { k: 'notrt', pts: p } : null;
  }
  if (/[=≠<>≤≥]/u.test(s)) return { k: 'metric', text: s };
  return null;
}

// ── Figure ─────────────────────────────────────────────────────────────────
export function makeFigure(points) {
  const P = points ?? {};
  const pt = (n) => (P[n] ? { x: P[n][0], y: P[n][1] } : null);
  let scale = 1;
  for (const a of Object.values(P)) scale = Math.max(scale, Math.abs(a[0]), Math.abs(a[1]));
  const eps = 1e-6 * scale * scale;
  const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
  const cross = (u, v) => u.x * v.y - u.y * v.x;
  const dot = (u, v) => u.x * v.x + u.y * v.y;
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const names = Object.keys(P);
  const collinear = (a, b, c) => Math.abs(cross(sub(b, a), sub(c, a))) <= eps;
  /** clé d'une droite (AB) : les points de la figure qui sont dessus — (AB) et (AJ) sont la même si J ∈ (AB) */
  const lineKey = ([a, b]) => {
    const A = pt(a),
      B = pt(b);
    if (!A || !B || dist(A, B) === 0) return null;
    return names
      .filter((n) => collinear(A, B, pt(n)))
      .sort()
      .join(',');
  };
  return { pt, sub, cross, dot, dist, eps, names, collinear, lineKey, points: P };
}

const segName = (a, b) => (a < b ? `${a}${b}` : `${b}${a}`);

/** Table du lecteur avec les noms de segments (« AB ») comme identifiants. */
export function metricTable(table, names) {
  const ids = [];
  for (const a of names) for (const b of names) if (a !== b) ids.push(`${a}${b}`);
  return withIdentifiers(table, ids);
}

function nodeName(table, ev) {
  return Object.keys(table.nodes).find((k) => table.nodes[k].eval === ev);
}

/** Relation de longueurs → { rel: 'eq'|'ne'|…, sides: [ast, ast], segs: Set } (segments renommés AB = BA) */
export function readMetric(text, G) {
  const { mtable: T, names } = G;
  let ast;
  try {
    ast = parse(text, T);
  } catch {
    return null;
  }
  const rel = T.nodes[ast.t]?.relation ?? T.nodes[ast.t]?.eval;
  if (!rel || isLeaf(ast) || ast.k.length !== 2) return null;
  const segs = new Set();
  let bad = false;
  const ren = (n) => {
    if (n.t === 'Sym') {
      const p = ptsOf(n.name);
      if (p.length === 2 && p.join('') === n.name && p.every((x) => names.includes(x))) {
        const s = segName(p[0], p[1]);
        segs.add(s);
        return { t: 'Sym', name: s };
      }
      bad = true;
      return n;
    }
    return isLeaf(n) ? n : { ...n, k: n.k.map(ren) };
  };
  const sides = ast.k.map(ren);
  if (bad) return null;
  return { rel, sides, segs, ast };
}

/** Clés d'une relation de longueurs, insensibles à l'ordre des membres et au produit en croix. */
export function metricKeys(M, T, cross = true) {
  const ADD = nodeName(T, 'add'),
    NEG = nodeName(T, 'neg'),
    MUL = nodeName(T, 'mul'),
    DIV = nodeName(T, 'div');
  const [a, b] = M.sides;
  const isDiv = (n) => n.t === DIV;
  const diffs = [[a, b]];
  if (cross && (isDiv(a) || isDiv(b))) {
    const num = (n) => (isDiv(n) ? n.k[0] : n),
      den = (n) => (isDiv(n) ? n.k[1] : { t: 'Num', p: 1, q: 1 });
    diffs.push([
      { t: MUL, k: [num(a), den(b)] },
      { t: MUL, k: [num(b), den(a)] },
    ]);
  }
  const keys = new Set();
  for (const [x, y] of diffs) {
    keys.add(M.rel + ':' + keyOf(light({ t: ADD, k: [x, { t: NEG, k: [y] }] }, T)));
    keys.add(M.rel + ':' + keyOf(light({ t: ADD, k: [y, { t: NEG, k: [x] }] }, T)));
  }
  return keys;
}

const sameKeys = (A, B) => [...A].some((k) => B.has(k));

/** Vérité d'une propriété sur la figure (null : un point inconnu). */
export function propTrue(p, G) {
  const F = G.fig;
  const P = (n) => F.pt(n);
  const need = (...ns) => ns.every((n) => P(n));
  const near = (u, v) => Math.abs(u - v) <= 1e-6 * Math.max(1, Math.abs(u), Math.abs(v));
  switch (p.k) {
    case 'par':
    case 'npar':
    case 'perp': {
      if (!need(...p.a, ...p.b)) return null;
      const u = F.sub(P(p.a[1]), P(p.a[0])),
        v = F.sub(P(p.b[1]), P(p.b[0]));
      if (p.k === 'perp') return Math.abs(F.dot(u, v)) <= F.eps;
      const par = Math.abs(F.cross(u, v)) <= F.eps;
      return p.k === 'par' ? par : !par;
    }
    case 'mid': {
      if (!need(p.i, ...p.seg)) return null;
      const I = P(p.i),
        A = P(p.seg[0]),
        B = P(p.seg[1]);
      return near(I.x, (A.x + B.x) / 2) && near(I.y, (A.y + B.y) / 2);
    }
    case 'sym': {
      if (!need(p.m, p.a, p.o)) return null;
      const M = P(p.m),
        A = P(p.a),
        O = P(p.o);
      return near(O.x, (A.x + M.x) / 2) && near(O.y, (A.y + M.y) / 2);
    }
    case 'onseg':
    case 'online':
    case 'bis':
    case 'circ': {
      if (!need(p.p, ...p.seg)) return null;
      const M = P(p.p),
        A = P(p.seg[0]),
        B = P(p.seg[1]);
      if (p.k === 'bis') return near(F.dist(M, A), F.dist(M, B));
      if (p.k === 'circ') return Math.abs(F.dot(F.sub(A, M), F.sub(B, M))) <= F.eps;
      if (!F.collinear(A, B, M)) return false;
      return p.k === 'online' || F.dot(F.sub(A, M), F.sub(B, M)) <= F.eps;
    }
    case 'quad': {
      if (!need(...p.pts)) return null;
      const [A, B, C, D] = p.pts.map(P);
      const para = near(A.x + C.x, B.x + D.x) && near(A.y + C.y, B.y + D.y);
      if (!para) return false;
      const right = Math.abs(F.dot(F.sub(B, A), F.sub(C, B))) <= F.eps;
      const rhomb = near(F.dist(A, B), F.dist(B, C));
      if (p.shape === 'rectangle') return right;
      if (p.shape === 'losange') return rhomb;
      if (p.shape === 'carré') return right && rhomb;
      return true;
    }
    case 'rt':
    case 'iso': {
      if (!need(p.at, ...p.others)) return null;
      const A = P(p.at),
        B = P(p.others[0]),
        C = P(p.others[1]);
      return p.k === 'rt'
        ? Math.abs(F.dot(F.sub(B, A), F.sub(C, A))) <= F.eps
        : near(F.dist(A, B), F.dist(A, C));
    }
    case 'notrt': {
      if (!need(...p.pts)) return null;
      const [A, B, C] = p.pts.map(P);
      const r = (X, Y, Z) => Math.abs(F.dot(F.sub(Y, X), F.sub(Z, X))) <= F.eps;
      return !r(A, B, C) && !r(B, A, C) && !r(C, A, B);
    }
    case 'metric': {
      const M = readMetric(p.text, G);
      if (!M) return null;
      const env = {};
      for (const s of M.segs) {
        const q = ptsOf(s);
        env[s] = F.dist(P(q[0]), P(q[1]));
      }
      return metricHolds(M, env, G.table);
    }
  }
  return null;
}

export function metricHolds(M, env, table) {
  let a, b;
  try {
    a = toF(evaluate(M.sides[0], env, table));
    b = toF(evaluate(M.sides[1], env, table));
  } catch {
    return null;
  }
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const eq = Math.abs(a - b) <= 1e-6 * Math.max(1, Math.abs(a), Math.abs(b));
  switch (M.rel) {
    case 'eq':
      return eq;
    case 'ne':
      return !eq;
    case 'lt':
      return a < b && !eq;
    case 'le':
      return a < b || eq;
    case 'gt':
      return a > b && !eq;
    case 'ge':
      return a > b || eq;
  }
  return null;
}

// ── Mêmes propriétés ────────────────────────────────────────────────────────
const sameSet = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
const DIHEDRAL = [
  [0, 1, 2, 3],
  [1, 2, 3, 0],
  [2, 3, 0, 1],
  [3, 0, 1, 2],
  [3, 2, 1, 0],
  [2, 1, 0, 3],
  [1, 0, 3, 2],
  [0, 3, 2, 1],
];

/** Deux propriétés (sans variables) disent-elles la même chose ? */
export function sameProp(p, q, G) {
  if (p.k !== q.k) {
    // « M ∈ [AB] » dit aussi « M ∈ (AB) »
    if (p.k === 'online' && q.k === 'onseg') return p.p === q.p && sameLine(p.seg, q.seg, G);
    return false;
  }
  const sl = (l1, l2) => sameLine(l1, l2, G);
  switch (p.k) {
    case 'par':
    case 'npar':
    case 'perp':
      return (sl(p.a, q.a) && sl(p.b, q.b)) || (sl(p.a, q.b) && sl(p.b, q.a));
    case 'mid':
      return p.i === q.i && sameSet(p.seg, q.seg);
    case 'sym':
      return p.m === q.m && p.a === q.a && p.o === q.o;
    case 'onseg':
    case 'bis':
    case 'circ':
      return p.p === q.p && sameSet(p.seg, q.seg);
    case 'online':
      return p.p === q.p && sl(p.seg, q.seg);
    case 'quad':
      return p.shape === q.shape && DIHEDRAL.some((o) => o.every((j, i) => p.pts[i] === q.pts[j]));
    case 'rt':
    case 'iso':
      return p.at === q.at && sameSet(p.others, q.others);
    case 'notrt':
      return sameSet(p.pts, q.pts);
    case 'metric': {
      const a = readMetric(p.text, G),
        b = readMetric(q.text, G);
      return !!a && !!b && sameKeys(metricKeys(a, G.table, false), metricKeys(b, G.table, false));
    }
  }
  return false;
}
function sameLine(l1, l2, G) {
  const a = G.fig.lineKey(l1),
    b = G.fig.lineKey(l2);
  return a !== null && a === b;
}

// ── Règles : motifs à variables ─────────────────────────────────────────────
const VAR_RE = /\?(?:[A-Z]|d\d)/gu;
const instText = (text, B) => String(text).replace(VAR_RE, (v) => B[v] ?? v);

/** Motif à variables de DROITE (« (?d1) ⊥ (?d3) ») : liaison directe aux droites du fait (alignement compris). */
function matchLineVars(pat, patText, f, B, G) {
  const m = /^\((\?d\d)\)\s*(\/\/|⊥|∦)\s*\((\?d\d)\)$/u.exec(patText.trim());
  const q = f.prop;
  if (!m || !['par', 'perp', 'npar'].includes(q.k) || q.k !== pat.k) return [];
  const out = [];
  for (const [x, y] of [
    [q.a, q.b],
    [q.b, q.a],
  ]) {
    const b = { ...B };
    let ok = true;
    for (const [v, l] of [
      [m[1], x],
      [m[3], y],
    ]) {
      if (v in b) ok = ok && sameLine(two(b[v]), l, G);
      else b[v] = l.join('');
    }
    if (ok && (m[1] !== m[3] || sameLine(x, y, G))) out.push(b);
  }
  return out;
}

/** Toutes les façons d'étendre les liaisons B pour que le motif (texte) décrive le fait f. */
function matchPattern(patText, f, B, G) {
  if (/\?d\d/u.test(patText)) {
    const k = /⊥/u.test(patText) ? 'perp' : /∦/u.test(patText) ? 'npar' : 'par';
    return matchLineVars({ k }, patText, f, B, G);
  }
  const pat = parseProp(patText);
  if (!pat) return [];
  const out = [];
  const vars = [...new Set((patText.match(VAR_RE) ?? []).filter((v) => !(v in B)))];
  // points candidats : ceux du fait (et de la figure pour un motif de droite, alignement compris)
  const fpts =
    f.k === 'metric'
      ? [...(readMetric(f.text, G)?.segs ?? [])].flatMap(ptsOf)
      : ptsOf(f.text ?? '').length
        ? ptsOf(f.text)
        : [];
  const lineish = ['par', 'npar', 'perp', 'online'].includes(pat.k);
  const pool = [...new Set(lineish ? G.fig.names : fpts)];
  if (vars.length > 4 || pool.length ** vars.length > 20000) return out;
  const rec = (i, b) => {
    if (i === vars.length) {
      const t = instText(patText, b);
      if (/\?/u.test(t)) return;
      const q = parseProp(t);
      if (q && sameProp(q, f.prop, G)) out.push(b);
      return;
    }
    for (const n of pool) {
      if (Object.values(b).includes(n)) continue;
      rec(i + 1, { ...b, [vars[i]]: n });
    }
  };
  rec(0, B);
  return out;
}

function whenOk(rule, B, G) {
  for (const w of rule.when ?? []) {
    if (w.longest) {
      const [h, ...o] = w.longest.map((s) => ptsOf(instText(s, B)));
      const d = (q) => G.fig.dist(G.fig.pt(q[0]), G.fig.pt(q[1]));
      if (!o.every((q) => d(h) >= d(q) - 1e-9)) return false;
    }
  }
  return true;
}

/**
 * Règle qui justifie la propriété `prop` à partir des faits (une seule application) : la règle, ou null.
 * facts : [{ text, prop }]
 */
export function ruleFor(prop, facts, G) {
  for (const rule of G.rules) {
    for (const thenPat of rule.then) {
      const tp = /\?d\d/u.test(thenPat)
        ? { k: /⊥/u.test(thenPat) ? 'perp' : /∦/u.test(thenPat) ? 'npar' : 'par' }
        : parseProp(thenPat);
      if (!tp || (tp.k !== prop.k && !(tp.k === 'online' && prop.k === 'onseg'))) continue;
      const rec = (i, B) => {
        if (i === rule.if.length) {
          const t = instText(thenPat, B);
          if (/\?/u.test(t)) {
            // variables seulement dans la conclusion : on les lit sur la ligne
            return (
              matchPattern(thenPat, { text: prop.text, prop }, B, G).find((b2) =>
                whenOk(rule, b2, G),
              ) ?? null
            );
          }
          const q = parseProp(t);
          return q && sameProp(q, prop, G) && whenOk(rule, B, G) ? B : null;
        }
        for (const f of facts)
          for (const b2 of matchPattern(rule.if[i], f, B, G)) {
            const r = rec(i + 1, b2);
            if (r) return r;
          }
        return null;
      };
      if (rec(0, {})) return rule;
    }
  }
  return null;
}

/**
 * Une relation de longueurs vraie se justifie-t-elle par un calcul ? On remplace les segments égaux (« IA = IB ») par
 * un même nom et les écritures de valeur connue (« AB = 8 », « AN ÷ AC = 0,7 ») par leur valeur ; puis la relation
 * ne contient plus de longueur inconnue, ou elle redit une relation écrite, ou elle donne la seule longueur
 * inconnue d'une relation écrite (« MB² = 40,96 » de « AB² = AM² + MB² »).
 */
export function computeJustified(prop, facts, G) {
  const T = G.table;
  const M = readMetric(prop.text, G);
  if (!M) return false;
  const Ms = facts
    .filter((f) => f.prop.k === 'metric')
    .map((f) => readMetric(f.prop.text, G))
    .filter(Boolean);
  // segments égaux : une classe, un nom
  const parent = new Map();
  const find = (x) => {
    while (parent.has(x) && parent.get(x) !== x) x = parent.get(x);
    return x;
  };
  for (const F of Ms) {
    const [a, b] = F.sides;
    if (
      F.rel === 'eq' &&
      a.t === 'Sym' &&
      b.t === 'Sym' &&
      F.segs.has(a.name) &&
      F.segs.has(b.name)
    ) {
      const ra = find(a.name),
        rb = find(b.name);
      if (ra !== rb) parent.set(ra < rb ? rb : ra, ra < rb ? ra : rb);
    }
  }
  const ren = (n) =>
    n.t === 'Sym' ? { t: 'Sym', name: find(n.name) } : isLeaf(n) ? n : { ...n, k: n.k.map(ren) };
  const noSeg = (n) => n.t !== 'Sym' && (isLeaf(n) || n.k.every(noSeg));
  const valueOf = (n) => {
    try {
      const x = toF(evaluate(n, {}, T));
      return Number.isFinite(x) ? x : null;
    } catch {
      return null;
    }
  };
  // écritures de valeur connue
  const known = new Map();
  for (const F of Ms) {
    if (F.rel !== 'eq') continue;
    const [a, b] = F.sides.map(ren);
    for (const [x, y] of [
      [a, b],
      [b, a],
    ]) {
      if (!noSeg(x) && noSeg(y)) {
        const v = valueOf(y);
        if (v !== null) known.set(keyOf(light(x, T)), v);
      }
    }
  }
  const subst = (n) => {
    const k = keyOf(light(n, T));
    if (!noSeg(n) && known.has(k)) return numOf(known.get(k));
    return isLeaf(n) ? n : { ...n, k: n.k.map(subst) };
  };
  const reduce = (X) => ({ ...X, sides: X.sides.map((s0) => subst(ren(s0))) });
  const segsIn = (n, out = new Set()) => {
    if (n.t === 'Sym') out.add(n.name);
    else if (!isLeaf(n)) n.k.forEach((c) => segsIn(c, out));
    return out;
  };
  const L = reduce(M);
  const Lsegs = new Set([...segsIn(L.sides[0]), ...segsIn(L.sides[1])]);
  if (!Lsegs.size) return true;
  const kL = metricKeys(L, T);
  if (L.rel === 'eq' && keyOf(light(L.sides[0], T)) === keyOf(light(L.sides[1], T))) return true;
  // la seule inconnue s, écrite « s = … » ou « sᵏ = … »
  let solved = null;
  if (L.rel === 'eq' && Lsegs.size === 1) {
    const s = [...Lsegs][0];
    for (const [x, y] of [
      [L.sides[0], L.sides[1]],
      [L.sides[1], L.sides[0]],
    ]) {
      if (!noSeg(y)) continue;
      const v = valueOf(y);
      if (v === null) continue;
      if (x.t === 'Sym' && x.name === s) solved = { s, v };
      else if (
        T.nodes[x.t]?.eval === 'pow' &&
        x.k[0].t === 'Sym' &&
        x.k[0].name === s &&
        x.k[1].t === 'Num' &&
        v >= 0
      )
        solved = { s, v: v ** (x.k[1].q / x.k[1].p) };
    }
  }
  for (const F0 of Ms) {
    const F = reduce(F0);
    if (sameKeys(kL, metricKeys(F, T))) return true;
    if (solved) {
      const Fs = new Set([...segsIn(F.sides[0]), ...segsIn(F.sides[1])]);
      if (Fs.size === 1 && Fs.has(solved.s) && metricHolds(F, { [solved.s]: solved.v }, T))
        return true;
    }
  }
  return false;
}

function numOf(x) {
  // valeur décimale courte → fraction exacte (les longueurs connues viennent de nombres écrits)
  for (let q = 1; q <= 10000; q *= 10) {
    const p = Math.round(x * q);
    if (Math.abs(p / q - x) < 1e-12) return { t: 'Num', p, q };
  }
  return { t: 'Num', p: Math.round(x * 1e6), q: 1e6 };
}
